import { MAX_UPLOAD_BYTES } from '../../shared/files';
import { useEffect, useState } from 'preact/hooks';
import {
  CONDITIONS,
  type Condition,
  type DeliveryConfirmedBody,
  type DeliveryLineView,
  type DeliveryPageBody,
} from '../../shared/delivery/delivery';
import { useI18n, type MessageKey } from '../i18n';
import { ApiError, apiGet, apiSend, apiUpload } from '../lib/api';
import { formatDate } from '../lib/dates';

// Confirm delivery (screen 14, P4.1). The requester (or Procurement on their behalf) counts what
// arrived on the issued PO and its condition, rates the supplier and may attach the supplier's
// signed note. Each confirmation creates delivery note k of the PO; the broker refuses
// over-receipt and only counts items in good condition (D57).

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; code: string; reference: string | null }
  | { kind: 'ok'; data: DeliveryPageBody };

interface Row {
  qty: string;
  condition: Condition;
}

const fmt = (v: number) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 3 }).format(v);
const parse = (s: string) => (s.trim() === '' ? 0 : Number(s));
const FILE_TYPES = '.pdf,.jpg,.jpeg,.png';
const MAX_FILE = MAX_UPLOAD_BYTES;

export function ConfirmDeliveryPage({ prNo }: { prNo: string }) {
  const { t, lang } = useI18n();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [reload, setReload] = useState(0);
  const [rows, setRows] = useState<Record<number, Row>>({});
  const [rating, setRating] = useState(0);
  const [ref, setRef] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string; ref?: string | null } | null>(null);
  const [asking, setAsking] = useState(false);
  const [asked, setAsked] = useState(false);

  // Procurement asks the requester to confirm it themselves (D94) instead of confirming for them.
  async function askRequester() {
    setAsking(true);
    setNote(null);
    try {
      const out = await apiSend<{ emailed: boolean }>(
        'POST',
        `/api/prs/${encodeURIComponent(prNo)}/delivery/ask`,
        {},
      );
      setAsked(out.emailed);
      setNote(
        out.emailed ? { ok: true, text: t('dl.asked') } : { ok: false, text: t('dl.askNotSent') },
      );
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      setNote({ ok: false, text: t('dl.askNotSent'), ref: e?.correlationId ?? null });
    } finally {
      setAsking(false);
    }
  }
  const [problemOpen, setProblemOpen] = useState(false);
  const [problem, setProblem] = useState('');

  useEffect(() => {
    let live = true;
    apiGet<DeliveryPageBody>(`/api/prs/${encodeURIComponent(prNo)}/delivery`)
      .then((data) => {
        if (!live) return;
        setLoad({ kind: 'ok', data });
        setRows(
          Object.fromEntries(
            data.lines.map((l) => [
              l.lineNo,
              { qty: l.outstanding > 0 ? String(l.outstanding) : '0', condition: 'good' },
            ]),
          ),
        );
      })
      .catch((err: unknown) => {
        if (!live) return;
        const e = err instanceof ApiError ? err : null;
        setLoad({ kind: 'error', code: e?.code ?? 'generic', reference: e?.correlationId ?? null });
      });
    return () => {
      live = false;
    };
  }, [prNo, reload]);

  if (load.kind === 'loading') {
    return (
      <section class="card" aria-busy="true">
        <p>{t('req.loading')}</p>
      </section>
    );
  }
  if (load.kind === 'error') {
    return (
      <section class="card card--error" role="alert">
        <p>
          {load.code === 'invalid_transition'
            ? t('dl.error.noPo')
            : load.code === 'not_found'
              ? t('dl.error.notFound')
              : t('req.loadError')}
        </p>
        {load.reference && (
          <p class="caption" dir="ltr">
            {t('error.reference', { ref: load.reference })}
          </p>
        )}
      </section>
    );
  }

  const data = load.data;
  const row = (l: DeliveryLineView): Row => rows[l.lineNo] ?? { qty: '0', condition: 'good' };
  const setRow = (lineNo: number, patch: Partial<Row>) =>
    setRows({
      ...rows,
      [lineNo]: { ...(rows[lineNo] ?? { qty: '0', condition: 'good' }), ...patch },
    });

  // Client-side preview of the broker's rules (it checks again): what stays outstanding.
  const lineError = (l: DeliveryLineView): MessageKey | null => {
    const q = parse(row(l).qty);
    if (!Number.isFinite(q) || q < 0) return 'dl.err.number';
    if (q > l.outstanding) return 'dl.err.over';
    return null;
  };
  const left = (l: DeliveryLineView) => {
    const r = row(l);
    const q = parse(r.qty);
    const good = r.condition === 'good' && Number.isFinite(q) ? q : 0;
    return Math.max(0, Math.round((l.outstanding - good) * 1000) / 1000);
  };
  const stillToCome = data.lines.filter((l) => left(l) > 0);
  const anyArrived = data.lines.some((l) => parse(row(l).qty) > 0);
  const anyBad = data.lines.some((l) => parse(row(l).qty) > 0 && row(l).condition !== 'good');
  const seq = data.deliveries.length + 1;

  async function confirm() {
    const errs: Record<string, string> = {};
    for (const l of data.lines) {
      const e = lineError(l);
      if (e) errs[`line.${l.lineNo}`] = t(e, { n: fmt(l.outstanding) });
    }
    if (!anyArrived) errs.lines = t('dl.err.nothing');
    if (rating < 1) errs.rating = t('dl.err.rating');
    if (anyBad && !notes.trim()) errs.notes = t('dl.err.notesNeeded');
    if (file && file.size > MAX_FILE) errs.file = t('dl.err.fileSize');
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      setNote({ ok: false, text: t('dl.err.fix') });
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      const out = await apiSend<DeliveryConfirmedBody>(
        'POST',
        `/api/prs/${encodeURIComponent(prNo)}/delivery`,
        {
          etag: data.etag,
          lines: data.lines.map((l) => ({
            lineNo: l.lineNo,
            qty: parse(row(l).qty),
            condition: row(l).condition,
          })),
          rating,
          supplierDnRef: ref.trim(),
          notes: notes.trim(),
        },
      );
      let text = t('dl.done', { no: out.dnNo, n: String(out.seq) });
      let ok = true;
      if (file) {
        try {
          await apiUpload(`/api/prs/${encodeURIComponent(prNo)}/delivery/file`, file, {
            'x-dn-no': out.dnNo,
          });
        } catch {
          text = `${text} ${t('dl.fileFailed')}`;
          ok = false;
        }
      }
      // The PDF is made in its own request (free plan subrequest budget); a failure leaves the
      // DN valid, and the document page can file it again.
      try {
        const filed = await apiSend<{ pdf: boolean }>(
          'POST',
          `/api/dns/${encodeURIComponent(out.dnNo)}/publish`,
          {},
        );
        if (!filed.pdf) text = `${text} ${t('dl.pdfLater')}`;
      } catch {
        text = `${text} ${t('dl.pdfLater')}`;
      }
      setNote({ ok, text });
      setFile(null);
      setRating(0);
      setRef('');
      setNotes('');
      setReload((r) => r + 1);
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      setErrors(Object.fromEntries(Object.keys(e?.fields ?? {}).map((k) => [k, t('dl.err.line')])));
      setNote({
        ok: false,
        text:
          e?.code === 'conflict'
            ? t('dl.err.conflict')
            : e?.code === 'invalid_input'
              ? t('dl.err.fix')
              : e?.code === 'invalid_transition'
                ? t('dl.err.nothingOpen')
                : t('dl.err.save'),
        ref: e?.correlationId ?? null,
      });
    } finally {
      setBusy(false);
    }
  }

  async function report() {
    if (!problem.trim()) {
      setErrors({ problem: t('dl.err.problem') });
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      await apiSend('POST', `/api/prs/${encodeURIComponent(prNo)}/delivery/problem`, {
        notes: problem.trim(),
      });
      setNote({ ok: true, text: t('dl.problemSent') });
      setProblem('');
      setProblemOpen(false);
      setErrors({});
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      setNote({ ok: false, text: t('dl.err.save'), ref: e?.correlationId ?? null });
    } finally {
      setBusy(false);
    }
  }

  const edit = data.canConfirm;

  return (
    <div class="stack">
      <p class="caption">
        <a href="#/requests">{t('nav.myRequests')}</a> /{' '}
        <a href={`#/requests/${prNo}`} dir="ltr">
          {prNo}
        </a>{' '}
        / <span dir="ltr">{data.poNo}</span>
      </p>
      <h2 class="detail-title" dir="auto">
        {data.title}
      </h2>
      <p class="caption">
        <span dir="auto">{data.supplierName}</span>
        {edit && ` · ${t('dl.lead')}`}
      </p>
      {data.onBehalf && (
        <div class="notice notice--action">
          <p>{t('dl.onBehalf', { name: data.requesterName || t('dl.theRequester') })}</p>
          <button
            type="button"
            class="button button--primary"
            disabled={asking || asked}
            onClick={() => void askRequester()}
          >
            {asked
              ? t('dl.askedShort')
              : t('dl.ask', { name: data.requesterName || t('dl.theRequester') })}
          </button>
        </div>
      )}
      {!edit && (
        <p class="notice notice--info">
          {data.state === 'full' ? t('dl.allReceived') : t('dl.readOnly')}
        </p>
      )}
      {note && (
        <div class={note.ok ? 'form-saved' : 'form-problem'} role={note.ok ? 'status' : 'alert'}>
          <p>{note.text}</p>
          {note.ref && (
            <p class="caption" dir="ltr">
              {t('error.reference', { ref: note.ref })}
            </p>
          )}
        </div>
      )}

      <section class="card" aria-labelledby="dl-items">
        <h3 id="dl-items" class="visually-hidden">
          {t('dl.items')}
        </h3>
        {data.lines.length === 0 ? (
          <p>{t('dl.noItems')}</p>
        ) : (
          <div class="table-wrap">
            <table class="table dl-table">
              <thead>
                <tr>
                  <th scope="col">{t('dl.col.item')}</th>
                  <th scope="col" class="num dl-wide-only">
                    {t('dl.col.ordered')}
                  </th>
                  <th scope="col" class="num dl-wide-only">
                    {t('dl.col.before')}
                  </th>
                  {edit && <th scope="col">{t('dl.col.now')}</th>}
                  <th scope="col">{t('dl.col.condition')}</th>
                </tr>
              </thead>
              <tbody>
                {data.lines.map((l) => {
                  const r = row(l);
                  const err = errors[`line.${l.lineNo}`];
                  const errId = `dl-err-${l.lineNo}`;
                  const rest = edit ? left(l) : l.outstanding;
                  return (
                    <tr key={l.lineNo}>
                      <td>
                        <span dir="auto">{l.description}</span>
                        {l.unit && (
                          <span class="caption cell-sub">
                            {t(`pr.unit.${l.unit}` as MessageKey)}
                          </span>
                        )}
                        <span class="caption cell-sub dl-phone-only">
                          {t('dl.orderedBefore', {
                            ordered: fmt(l.ordered),
                            before: fmt(l.receivedBefore),
                          })}
                        </span>
                      </td>
                      <td class="num dl-wide-only" dir="ltr">
                        {fmt(l.ordered)}
                      </td>
                      <td class="num dl-wide-only" dir="ltr">
                        {fmt(l.receivedBefore)}
                      </td>
                      {edit && (
                        <td>
                          {l.outstanding > 0 ? (
                            <div class="dl-now">
                              <input
                                class="po-input po-input--num"
                                type="number"
                                min={0}
                                max={l.outstanding}
                                step="any"
                                dir="ltr"
                                aria-label={t('dl.aria.now', { item: l.description })}
                                aria-invalid={err ? true : undefined}
                                aria-describedby={err ? errId : undefined}
                                value={r.qty}
                                onInput={(e) =>
                                  setRow(l.lineNo, { qty: (e.target as HTMLInputElement).value })
                                }
                              />
                              {err && (
                                <p class="field__error" id={errId}>
                                  {err}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span class="caption">—</span>
                          )}
                        </td>
                      )}
                      <td>
                        <div class="dl-cond">
                          {edit && l.outstanding > 0 && parse(r.qty) > 0 && (
                            <select
                              class="po-input"
                              aria-label={t('dl.aria.condition', { item: l.description })}
                              value={r.condition}
                              onChange={(e) =>
                                setRow(l.lineNo, {
                                  condition: (e.target as HTMLSelectElement).value as Condition,
                                })
                              }
                            >
                              {CONDITIONS.map((c) => (
                                <option key={c} value={c}>
                                  {t(`dl.cond.${c}` as MessageKey)}
                                </option>
                              ))}
                            </select>
                          )}
                          {rest > 0 ? (
                            <span class="pill pill--warning">
                              {t('dl.toCome', { n: fmt(rest) })}
                            </span>
                          ) : (
                            <span class="pill pill--success">{t('dl.complete')}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {errors.lines && <p class="field__error">{errors.lines}</p>}
      </section>

      {edit && (
        <>
          <div class="dl-layout">
            <section class="card stack" aria-labelledby="dl-paper">
              <h3 id="dl-paper" class="card__title">
                {t('dl.supplierNote')} <span class="caption">{t('dl.optional')}</span>
              </h3>
              <div class="field">
                <label for="dl-ref">{t('dl.ref')}</label>
                <input
                  id="dl-ref"
                  dir="ltr"
                  maxLength={60}
                  value={ref}
                  onInput={(e) => setRef((e.target as HTMLInputElement).value)}
                />
              </div>
              <div class="field">
                <label for="dl-file">{t('dl.file')}</label>
                <input
                  id="dl-file"
                  type="file"
                  accept={FILE_TYPES}
                  aria-describedby="dl-file-hint"
                  aria-invalid={errors.file ? true : undefined}
                  onChange={(e) => setFile((e.target as HTMLInputElement).files?.[0] ?? null)}
                />
                <p class="caption" id="dl-file-hint">
                  {t('dl.fileHint')}
                </p>
                {errors.file && <p class="field__error">{errors.file}</p>}
              </div>
              <div class="field">
                <label for="dl-notes">{t('dl.notes')}</label>
                <textarea
                  id="dl-notes"
                  rows={3}
                  maxLength={1000}
                  dir="auto"
                  placeholder={t('dl.notesHint')}
                  aria-invalid={errors.notes ? true : undefined}
                  value={notes}
                  onInput={(e) => setNotes((e.target as HTMLTextAreaElement).value)}
                />
                {errors.notes && <p class="field__error">{errors.notes}</p>}
              </div>
            </section>

            <section class="card stack" aria-labelledby="dl-rate">
              <fieldset class="dl-rating">
                <legend id="dl-rate" class="card__title">
                  {t('dl.rate')}
                </legend>
                <div class="dl-rating__options">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <label key={n} class={`dl-star${n <= rating ? ' dl-star--on' : ''}`}>
                      <input
                        type="radio"
                        name="dl-rating"
                        value={n}
                        checked={rating === n}
                        onChange={() => setRating(n)}
                      />
                      <span dir="ltr">{n}</span>
                    </label>
                  ))}
                </div>
                <p class="caption">{t('dl.rateHint')}</p>
                {errors.rating && <p class="field__error">{errors.rating}</p>}
              </fieldset>
              {anyArrived && (
                <p class={`notice ${stillToCome.length ? 'notice--action' : 'notice--info'}`}>
                  {stillToCome.length
                    ? t('dl.partialNote', {
                        n: String(seq),
                        items: stillToCome.map((l) => l.description).join(', '),
                      })
                    : t('dl.fullNote', { n: String(seq) })}
                </p>
              )}
            </section>
          </div>

          {problemOpen && (
            <section class="card stack" aria-labelledby="dl-problem">
              <h3 id="dl-problem" class="card__title">
                {t('dl.problem')}
              </h3>
              <div class="field">
                <label for="dl-problem-text">{t('dl.problemLabel')}</label>
                <textarea
                  id="dl-problem-text"
                  rows={3}
                  maxLength={1000}
                  dir="auto"
                  aria-invalid={errors.problem ? true : undefined}
                  value={problem}
                  onInput={(e) => setProblem((e.target as HTMLTextAreaElement).value)}
                />
                {errors.problem && <p class="field__error">{errors.problem}</p>}
              </div>
              <div class="actions">
                <button
                  type="button"
                  class="button"
                  disabled={busy}
                  onClick={() => setProblemOpen(false)}
                >
                  {t('dl.cancel')}
                </button>
                <button
                  type="button"
                  class="button button--danger"
                  disabled={busy}
                  onClick={() => void report()}
                >
                  {t('dl.problemSend')}
                </button>
              </div>
            </section>
          )}

          <div class="actions dl-actions">
            {!problemOpen && (
              <button
                type="button"
                class="button button--danger"
                disabled={busy}
                onClick={() => setProblemOpen(true)}
              >
                {t('dl.problem')}
              </button>
            )}
            <button
              type="button"
              class="button button--primary"
              disabled={busy}
              onClick={() => void confirm()}
            >
              {busy ? t('dl.saving') : t('dl.confirm')}
            </button>
          </div>
        </>
      )}

      {data.deliveries.length > 0 && (
        <section class="card" aria-labelledby="dl-done">
          <h3 id="dl-done" class="card__title">
            {t('dl.deliveries')}
          </h3>
          <ul class="action-list">
            {data.deliveries.map((d) => (
              <li key={d.dnNo} class="dl-note">
                <a dir="ltr" class="req-row__title" href={`#/dn/${d.dnNo}`}>
                  {d.dnNo}
                </a>
                <span class="caption">
                  {t('dl.seq', { n: String(d.seq) })} · {formatDate(d.receivedAt, lang)} ·{' '}
                  <span dir="ltr">{d.receivedBy}</span>
                  {d.supplierDnRef && (
                    <>
                      {' '}
                      · {t('dl.ref')} <span dir="ltr">{d.supplierDnRef}</span>
                    </>
                  )}
                </span>
                <span>
                  <span class={`pill ${d.partial ? 'pill--warning' : 'pill--success'}`}>
                    {d.partial ? t('dl.partial') : t('dl.full')}
                  </span>{' '}
                  {d.onBehalf && <span class="pill pill--neutral">{t('dl.byProcurement')}</span>}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
